import React, { useEffect, useMemo, useState, useRef } from "react";
import { connect } from "react-redux";
import { Base } from "zina-dogi-react";
import swal from "sweetalert";
import * as oet from "../../oet/redux";
import * as run from "../redux";
import { SSOTWebHeaderForm, SSOTItemTable } from "../components";
import {service as neoImporterService} from "../../../neo_importer";
import useBackwardData from "./use_backward_data";

/**
 * Container component that manages the SSOT Web form logic.
 *
 * This component brings together the header form (activity, sites, dates) and the item table (editable SSOT items).
 * It handles:
 * - Fetching initial OET and Run data.
 * - Processing form submission (searching NDPD items).
 * - Managing the state of the SSOT table data.
 * - Submitting the final SSOT items to the backend.
 * - Handling validation errors and redirects after successful creation.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.oet - The selected OET object from Redux state.
 * @param {Object} props.run - The selected Run object from Redux state.
 * @param {string} props.oetID - The OET ID from the URL parameters.
 * @param {string} props.runID - The Run ID from the URL parameters.
 * @param {boolean} props.loading - The loading state from Redux.
 * @param {Function} props.getDetails - Redux action to fetch OET details.
 * @param {Function} props.getRunDetail - Redux action to fetch Run details.
 * @param {Object} props.history - History object for navigation.
 */
const SSOTWebForm = props => {
    const cpo_data = props.oet ? props.oet.cpo_data : null;

    const { initialTableData, initialValues } = useBackwardData(props.location);

    const [data, setData] = useState(initialTableData);
    const [localLoading, setLocalLoading] = useState(false);
    const [isTableSubmitting, setIsTableSubmitting] = useState(false);
    const [form_data, setFormData] = useState({});
    const ssotTableRef = useRef(null);
    const ssotHeaderFormRef = useRef(null);

    useEffect(() => {
        getComponentData(props);
    }, [props.oetID, props.runID]);

    const base_op = useMemo(() => {
        return getBaseOp({ ...props, loading: props.loading || localLoading });
    }, [props.oet, props.run, props.loading, props.extraProps, cpo_data, localLoading]);
    
    return (
        <Base {...base_op}>
            <SSOTWebHeaderForm
                ref={ssotHeaderFormRef}
                oet={props.oet}
                run={props.run}
                initialValues={initialValues}
                submitLabel="Add Rows"
                activityDisabled={data.length > 0}
                disabled={isTableSubmitting}
                onSubmit={values => {
                    // Logic to merge new search results with existing table data.
                    // If a row exists in the current table (matched by activity, material, wp_id, site),
                    // we preserve the user's manual edits (subcontractor, synergy, PO instructions)
                    // instead of overwriting them with the raw search result.
                    const currentTableData = ssotTableRef.current
                        ? ssotTableRef.current.getData()
                        : [];
                    setLocalLoading(true);
                    run.service
                        .ndpdSearch(values)
                        .then(result => {
                            setLocalLoading(false);

                            const newData = result.data.map(newRow => {
                                const match = currentTableData.find(
                                    oldRow =>
                                        oldRow.activity_name === newRow.activity_name &&
                                        oldRow.material_number_item === newRow.material_number_item &&
                                        oldRow.wp_id === newRow.wp_id &&
                                        oldRow.site_id_name === newRow.site_id_name
                                );

                                if (match) {
                                    return {
                                        ...newRow,
                                        subcontractor_supplier: match.subcontractor_supplier,
                                        synergy: match.synergy,
                                        po_related_instruction: match.po_related_instruction,
                                        sog: match.sog,
                                    };
                                }
                                return newRow;
                            }).sort((a, b) => {
                                const siteA = String(a.site_id_name || "").toLowerCase();
                                const siteB = String(b.site_id_name || "").toLowerCase();
                                if (siteA < siteB) return -1;
                                if (siteA > siteB) return 1;
                                return 0;
                            });

                            if (ssotTableRef.current && ssotTableRef.current.clearFilters) {
                                ssotTableRef.current.clearFilters();
                            }
                            
                            setData(newData);
                            if (result.site_ids_to_clear && result.site_ids_to_clear.length > 0 && ssotHeaderFormRef.current) {
                                ssotHeaderFormRef.current.clearSiteIds(result.site_ids_to_clear);
                            }
                            if (result.errors && result.errors.length > 0) {
                                swal({
                                    title: "Warning",
                                    icon: "warning",
                                    text: result.errors.join("\n")
                                });
                            }
                            setFormData(values);
                        })
                        .catch(() => setLocalLoading(false));
                }}
                onClearForm={() => {
                    setData([]);
                }}
            />
            <SSOTItemTable
                ref={ssotTableRef}
                data={data}
                onSubmittingStateChange={setIsTableSubmitting}
                onSubmit={data => {
                    // Two-step submission process:
                    // 1. Send items to the backend to create/update the SSOT.
                    // 2. Trigger a validation file check (using the importer service) on the resulting ID.
                    // 3. Redirect the user to the validation report page.
                    return run.service.sendSSOTItems(props.oet.id, props.run.id, data, form_data).then((resp) => {
                        return neoImporterService
                            .validateFileUploaded(
                                "/importers/serviceordertemplateimporter/__pk__/validate-file/".replace('__pk__', resp.id)
                            )
                            .then(results => {
                                const redirectUrl = `/serviceorderitem/importer/${props.oet.id}/${props.run.id}/validate/${resp.id}/`;
                                props.history.push(redirectUrl, { source: "web_form" });
                                return results;
                            })
                            .catch(error => {
                                console.log({ error });
                                return error;
                            });
                    });
                }}
            />
        </Base>
    );
};

/**
 * Fetches the necessary data for the component when it mounts or when OET/RUN IDs change.
 *
 * Checks if the OET and Run details are already in the store and match the current IDs.
 * If not, it dispatches actions to fetch them.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.oet - Current OET object from store.
 * @param {Object} props.run - Current Run object from store.
 * @param {Function} props.getDetails - Action to fetch OET details.
 * @param {Function} props.getRunDetail - Action to fetch Run details.
 * @param {string} props.oetID - OET ID from URL parameters.
 * @param {string} props.runID - Run ID from URL parameters.
 * @param {Object} props.history - History object.
 */
const getComponentData = props => {
    const { oet, run, getDetails, history, getRunDetail, oetID, runID } = props;
    const hasOet = !!(oet && oet.id);
    const hasRun = !!(run && run.id);
    const get_run = res => {
        if (!hasRun || run.id !== runID) {
            getRunDetail(runID, oetID);
        }
    };
    if (oetID && runID) {
        if (!hasOet || oet.id !== oetID) {
            getDetails(oetID, history, runID).then(get_run);
        } else {
            get_run();
        }
    }
};

/**
 * Generates the base options and breadcrumbs for the Base component layout.
 *
 * @param {Object} param0 - Function parameters.
 * @param {Object} param0.oet - The OET object.
 * @param {Object} param0.run - The Run object.
 * @param {boolean} param0.loading - Loading state.
 * @param {Object} param0.extraProps - Any extra props to pass.
 * @returns {Object} Props for the Base component.
 */
function getBaseOp({ oet, run, loading, extraProps }) {
    const cpo_breadcrumb =
        oet && oet.cpo_data
            ? {
                  name: `CPO ${oet.cpo_data.number}`,
                  rel: `/detail/${oet.cpo_data.id}/`
              }
            : null;
    const breadcrumbs = [
        { name: "Home", rel: "/", icon: "home" },
        ...(cpo_breadcrumb ? [cpo_breadcrumb] : []),
        {
            name: `OET #${oet ? oet.id_zfill : ""}`,
            rel: oet ? `/tracker/${oet.id}/` : null
        },
        {
            name: `Run #${run ? run.number : ""}`,
            rel: run ? `/run/${oet.id}/${run.id}/` : null
        },
        {
            name: "SSOT Web Form"
        }
    ];

    return {
        className: "run_detail",
        // title: `${oet ? `OET #${oet.id_zfill}` : ""} - ${run ? `Run #${run.number}` : ""}`,
        breadcrumbs,
        extraProps,
        loading
    };
}

/**
 * Maps Redux state to component props.
 * Reads OET / Run selection, loading state, and URL params from the router match.
 *
 * @param {Object} store    - The global Redux store.
 * @param {Object} ownProps - Component's own props (includes `match.params` from React Router).
 * @returns {{ user: number, oet: Object, run: Object, oetID: string, runID: string, loading: boolean }}
 */
const mapStateToProps = (store, ownProps) => ({
    user: store.auth.user.id,
    oet: store.oet.oetSelected,
    run: store.oet.runSelected,
    oetID: ownProps.match.params.oetID,
    runID: ownProps.match.params.runID,
    loading: store.oet.loading
});

/**
 * Maps dispatch to component props.
 * Provides actions to load OET and Run details into the Redux store.
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {{ getDetails: Function, getRunDetail: Function }}
 */
const mapDispatchToProps = dispatch => ({
    getDetails: (oetID, history, runID) => {
        return dispatch(oet.actions.getOetInDetail(oetID, history, runID));
    },
    getRunDetail: (runID, oetID) => {
        dispatch(oet.actions.getRunDetail(runID, oetID, false));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(SSOTWebForm);
